import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Chip,
} from "@mui/material";
import {
  Inventory as InventoryIcon,
} from "@mui/icons-material";
import PageContainer from "../common/PageContainer";
import PageHeader from "../common/PageHeader";
import EmptyState from "../common/EmptyState";
const BASE_URL = process.env.REACT_APP_BASE_URL;

const MyInventories = () => {
  const [inventories, setInventories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchInventories = async () => {
      setLoading(true);
      setError(null);

      try {
        const token = sessionStorage.getItem("token");

        const response = await fetch(
          `${BASE_URL}/api/inventory/getmyinventories`,
          {
            method: "GET",
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          }
        );

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.message || "Failed to fetch inventories");
        }

        const data = await response.json();
        setInventories(data);
      } catch (error) {
        console.error("Error fetching inventories:", error);
        setError(error.message || "Something went wrong");
      } finally {
        setLoading(false);
      }
    };

    fetchInventories();
  }, []);

  if (loading) {
    return (
      <PageContainer>
        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", py: 12 }}>
          <CircularProgress size={40} sx={{ color: "#667eea", mb: 2 }} />
          <Typography sx={{ color: "#8b8fa3", fontSize: "0.875rem" }}>Loading your inventories...</Typography>
        </Box>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <PageHeader
        gradient
        title="My Inventory Assignments"
        subtitle="Items currently assigned to you"
        icon={<InventoryIcon />}
      />

      {inventories.length === 0 ? (
        <Paper elevation={0} sx={{ border: "1px solid #eef0f6", borderRadius: "16px" }}>
          <EmptyState
            title="No inventory items assigned"
            subtitle="No inventory items have been assigned to you yet."
          />
        </Paper>
      ) : (
        <Paper elevation={0} sx={{ border: "1px solid #eef0f6", borderRadius: "16px", overflow: "hidden" }}>
          <TableContainer sx={{ overflowX: "auto", maxWidth: "100%" }}>
            <Table>
              <TableHead>
                <TableRow>
                  {["ITEM NAME", "ITEM ID", "CATEGORY", "BRAND/MODEL", "ASSIGNED BY", "CONDITION", "PRICE", "ASSIGNMENT DATE", "RETURN DATE"].map((header) => (
                    <TableCell
                      key={header}
                      sx={{
                        bgcolor: "#f5f7fb",
                        fontWeight: 600,
                        fontSize: "0.75rem",
                        color: "#5a5f7a",
                        letterSpacing: "0.05em",
                        textTransform: "uppercase",
                        borderBottom: "2px solid #f0f0f5",
                        whiteSpace: "nowrap",
                        py: 1.5,
                      }}
                    >
                      {header}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {inventories.map((inventory) =>
                  inventory.assignmentHistory.map((assignment) => (
                    <TableRow
                      key={`${inventory._id}-${assignment.assignedTo}`}
                      sx={{
                        "&:hover": { bgcolor: "#f5f7fb" },
                        "& td": { borderBottom: "1px solid #f0f2f8" },
                      }}
                    >
                      <TableCell sx={{ fontSize: "0.875rem", color: "#1a1a2e", fontWeight: 600 }}>{inventory.itemName}</TableCell>
                      <TableCell sx={{ fontSize: "0.875rem", color: "#667eea", fontWeight: 600 }}>{inventory.itemID}</TableCell>
                      <TableCell>
                        <Chip label={inventory.category} size="small" variant="outlined" sx={{ fontSize: "0.75rem", borderColor: "#eef0f6", color: "#5a5f7a", height: 24 }} />
                      </TableCell>
                      <TableCell sx={{ fontSize: "0.875rem", color: "#1a1a2e" }}>{inventory.brandModel}</TableCell>
                      <TableCell sx={{ fontSize: "0.875rem", color: "#1a1a2e" }}>{assignment.assignedBy}</TableCell>
                      <TableCell>
                        <Chip
                          label={assignment.condition}
                          size="small"
                          sx={{
                            bgcolor: "#f5f7fb",
                            color: "#5a5f7a",
                            fontWeight: 600,
                            fontSize: "0.75rem",
                            height: 24,
                          }}
                        />
                      </TableCell>
                      <TableCell sx={{ fontSize: "0.875rem", color: "#1a1a2e", fontWeight: 600 }}>{inventory.price}</TableCell>
                      <TableCell sx={{ fontSize: "0.875rem", color: "#1a1a2e" }}>
                        {new Date(assignment.assignmentDate).toLocaleDateString()}
                      </TableCell>
                      <TableCell>
                        {assignment.returnDate ? (
                          <Typography sx={{ fontSize: "0.875rem", color: "#1a1a2e" }}>
                            {new Date(assignment.returnDate).toLocaleDateString()}
                          </Typography>
                        ) : (
                          <Chip
                            label="Not Returned"
                            size="small"
                            sx={{
                              bgcolor: "#FEF3C7",
                              color: "#92400E",
                              fontWeight: 600,
                              fontSize: "0.75rem",
                              height: 24,
                            }}
                          />
                        )}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}
    </PageContainer>
  );
};

export default MyInventories;
